import VueAuth from '@websanova/vue-auth/dist/v2/vue-auth.common.js';
import driverHttpAxios from '@websanova/vue-auth/dist/drivers/http/axios.1.x.js';
import driverRouterVueRouter from '@websanova/vue-auth/dist/drivers/router/vue-router.2.x.js';
import { isLocal } from '@/statics/environment';
import { mapCurrentUser } from '@/modules/User/models';
import { resolveFromURL } from '@/helper/productResolver';

const productFromLocalStorage = isLocal ? resolveFromURL() : '';
const buildTokenKey = (key) =>
  !productFromLocalStorage ? key : key + '_' + productFromLocalStorage;

export const AUTH_TOKEN_KEY_DEFAULT = buildTokenKey('fdl_auth_token_default');
export const REFRESH_TOKEN_KEY_DEFAULT = buildTokenKey('fdl_refresh_token_default');

export const AUTH_TOKEN_KEY_IMPERSONATE = buildTokenKey('fdl_auth_token_impersonate');
export const REFRESH_TOKEN_KEY_IMPERSONATE = buildTokenKey('fdl_refresh_token_impersonate');
export const ADMIN_TOKEN_KEY_IMPERSONATE = buildTokenKey('fdl_admin_token_impersonate');

export const IS_IMPERSONATED =
  window.location.pathname.startsWith('/impersonate') ||
  (!!sessionStorage.getItem(AUTH_TOKEN_KEY_IMPERSONATE) &&
    !!sessionStorage.getItem(REFRESH_TOKEN_KEY_IMPERSONATE));

export const AUTH_TOKEN_KEY = IS_IMPERSONATED ? AUTH_TOKEN_KEY_IMPERSONATE : AUTH_TOKEN_KEY_DEFAULT;

export const REFRESH_TOKEN_KEY = IS_IMPERSONATED
  ? REFRESH_TOKEN_KEY_IMPERSONATE
  : REFRESH_TOKEN_KEY_DEFAULT;

export const ADMIN_TOKEN_KEY = ADMIN_TOKEN_KEY_IMPERSONATE;

// TODO remove deprecated admin token keys and local storage cleanup after migration period
const DEPRECATED = {
  ADMIN_TOKEN_KEY_DEFAULT: buildTokenKey('admin'),
  ADMIN_REFRESH_TOKEN_KEY: buildTokenKey('refresh_token_admin'),
  AUTH_TOKEN_KEY_DEFAULT: buildTokenKey('auth_token_default'),
  REFRESH_TOKEN_KEY_DEFAULT: buildTokenKey('refresh_token_default'),
  AUTH_TOKEN_KEY_IMPERSONATE: buildTokenKey('auth_token_impersonate'),
  REFRESH_TOKEN_KEY_IMPERSONATE: buildTokenKey('refresh_token_impersonate'),
  ADMIN_TOKEN_KEY_IMPERSONATE: buildTokenKey('admin_impersonate')
};

Object.keys(DEPRECATED).forEach((key) => {
  localStorage.removeItem(DEPRECATED[key]);
});

/*
 Normal authentication tokens are stored in local storage, while impersonation tokens are kept in session storage to prevent authentication from being mixed between tabs.
 However, since vue-auth uses both storage types simultaneously, tokens can end up in both storages and may not be properly removed.
 Therefore, we need to clear both local and session storage to avoid issues with outdated or mismatched tokens.
*/
localStorage.removeItem(AUTH_TOKEN_KEY_IMPERSONATE);
localStorage.removeItem(REFRESH_TOKEN_KEY_IMPERSONATE);
localStorage.removeItem(ADMIN_TOKEN_KEY_IMPERSONATE);

sessionStorage.removeItem(AUTH_TOKEN_KEY_DEFAULT);
sessionStorage.removeItem(REFRESH_TOKEN_KEY_DEFAULT);

const REFRESH_API = 'token/refresh';

export default (Vue, router) => {
  const bearerAuth = {
    request: function (req, token) {
      if (req.url === REFRESH_API) {
        req.data = {
          refresh_token: this.token(this.options.refreshTokenKey),
          product: productFromLocalStorage || resolveFromURL()
        };
        return;
      }
      this.drivers.http.setHeaders.call(this, req, {
        Authorization: 'Bearer ' + token,
        ...(!!Vue.prototype.$auth.user()?.id && { 'X-User-ID': Vue.prototype.$auth.user()?.id }),
        ...(!!this.token(AUTH_TOKEN_KEY) && { 'X-User-Token': this.token(AUTH_TOKEN_KEY) }),
        ...(!!this.token(ADMIN_TOKEN_KEY) && { 'X-Admin-Token': this.token(ADMIN_TOKEN_KEY) })
      });
    },
    response: function (res) {
      const data = res.data || {};

      if (data.refresh_token) {
        this.token(this.options.refreshTokenKey, data.refresh_token);
      }

      return data.token;
    }
  };

  Vue.use(VueAuth, {
    plugins: {
      http: Vue.axios,
      router: router
    },
    drivers: {
      auth: bearerAuth,
      http: driverHttpAxios,
      router: driverRouterVueRouter
    },
    options: {
      tokenDefaultKey: AUTH_TOKEN_KEY,
      refreshTokenKey: REFRESH_TOKEN_KEY,
      stores: ['storage'],
      rolesKey: 'roles',
      loginData: {
        url: 'login',
        method: 'POST',
        redirect: '/',
        fetchUser: true,
        staySignedIn: true
      },
      fetchData: {
        url: 'user-management/user/current',
        method: 'GET',
        enabled: true
      },
      cookie: {
        HttpOnly: true
      },
      refreshData: {
        url: REFRESH_API,
        method: 'POST',
        enabled: true,
        interval: 10
      },
      parseUserData: (data) => {
        if (!data || Object.keys(data).length === 0) {
          return {};
        }

        return mapCurrentUser(data);
      }
    }
  });
};
